/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable eqeqeq */
import {
  filter, isEmpty, isNil, pickBy, propOr, prop, reject, map,
  uniqBy,
  pick,
} from 'ramda';
import { createSelector } from 'reselect';

import { GROUP_TABLES_CUSTOM_SORTING } from '../../../constants/minilobby';
import { GameGroup } from '../../reducers/config';
import { AuthGroup, LobbyTable } from '../../reducers/lobby';
import { RootState } from '../../types';
import { filtersSelector, generalConfigSelector, currentLanguageSelector } from '../config';
import { gameNameSelector } from '../game';
import { gamesLocaleSelector } from '../locale';
import { tableIdSelector } from '../table';
import { sortGroupTables } from './utils';

export const lobbySelector = (state: RootState) => state.lobby;

export const lobbyLoadingSelector = createSelector(
  lobbySelector,
  (lobby) => lobby.loading
);

export const lobbyOpenMenuWidgetSelector = createSelector(
  lobbySelector,
  (lobby) => lobby.openMenuWidget
);

export const lobbyOpenSelector = createSelector(
  lobbySelector,
  (lobby) => lobby.open
);

export const lobbyTitleSelector = createSelector(
  gamesLocaleSelector,
  gameNameSelector,
  (gamesLocale, gameName) => gamesLocale?.[gameName]
);

export const lobbyAllTablesSelector = createSelector(
  lobbySelector,
  (lobby) => lobby.tables
);

export const lobbyAuthGroupsSelector = createSelector(
  lobbySelector,
  (lobby) => lobby.authGroups
);

export const lobbyAllActiveTablesSelector = createSelector(
  tableIdSelector,
  lobbyAllTablesSelector,
  (currentTableId, allTables) => Object.values(allTables).reduce((acc, game) => {
    // @ts-ignore
    if (isNil(game) || isNil(game.tables) || isEmpty(game.tables)) return acc;
    // don't pick current game table or closed tables

    // @ts-ignore
    const gameTables = pickBy((table, key) => +key !== currentTableId && table.active, game.tables);

    // @ts-ignore
    return { ...acc, ...gameTables };
  }, {}) as Record<string, LobbyTable>

);

export const availableTableGroupsSelector = createSelector(
  tableIdSelector,
  lobbyAuthGroupsSelector,
  (activeTableId, authGroups) => filter<AuthGroup>((group) =>
    group.tables.includes(activeTableId))(Object.values(authGroups))
);

export const availableTableGroupsIdsSelector = createSelector(
  tableIdSelector,
  lobbyAuthGroupsSelector,
  (activeTableId, authGroups) => filter<AuthGroup>((group) =>
    group.tables.includes(activeTableId))(Object.values(authGroups))
    .map((el) => el.id).join(',')
);

export const lobbyCustomSortingConfigsSelector = createSelector(
  filtersSelector,
  (filters) => map(
    (customSort) => JSON.parse(propOr('{}', customSort, filters)),
    GROUP_TABLES_CUSTOM_SORTING
  ) as Record<string, unknown>[]
);

export const lobbyTablesSortingTypeConfigSelector = createSelector(
  filtersSelector,
  (filters) => filters?.tablesSortingType as string,
);

export const lobbyGameGroupsConfigSelector = createSelector(
  filtersSelector,
  (filters) => (filters?.gameGroups
    ? Object.values(filters.gameGroups).reduce(
      (acc, group) => { acc[group.id] = group; return acc; },
     {} as Record<string, GameGroup>
    ) : {}),
);

export const lobbyGamesGroupsSelector = createSelector(
  lobbyAuthGroupsSelector,
  lobbyAllActiveTablesSelector,
  lobbyGameGroupsConfigSelector,
  lobbyCustomSortingConfigsSelector,
  lobbyTablesSortingTypeConfigSelector,
  currentLanguageSelector,
  (authGroups, activeTables, gameGroupsConfig, customSortingConfigs, tablesSortingTypeConfig, language) => {
    const groupsWithActiveTables = Object.values(authGroups).reduce((accGroups, group) => {
      // avoid empty groups
      if (isEmpty(group.tables || {})) return accGroups;

      // take only group's live tables
      const groupLiveTables = reject(
        isNil,
        map((tableId) => activeTables[tableId], group.tables)
      );
      if (isEmpty(groupLiveTables)) return accGroups;

      accGroups[group.id] = {
        id: group.id,
        title: group.locale,
        tables: uniqBy(prop('id'), sortGroupTables({
          groupId: group.id,
          groupLiveTables,
          groupConfig: gameGroupsConfig[group.id],
          customSortingConfigs,
          tablesSortingTypeConfig,
          language: language.code,
        })),
      };
      return accGroups;
    }, {} as Record<string, Pick<AuthGroup, 'id'> & { title: string, tables: LobbyTable[] }>);

    return groupsWithActiveTables;
  }
);

export const lobbyGamesGroupTitlesSelector = createSelector(
  lobbyGamesGroupsSelector,
  // @ts-ignore
  (gameGroups) => map(pick([ 'title', 'id' ]), gameGroups)
);

export const lobbyGamesGroupIdsKeySelector = createSelector(
  lobbyGamesGroupsSelector,
  (gameGroups) => Object.keys(gameGroups).join(',')
);

export const lobbyOpenTableMethod = createSelector(
  generalConfigSelector,
  prop('openTableMethod')
);
